import {
  Banner,
  Box,
  DropdownButtonContext,
  IconButton,
  Tooltip,
} from '@dixa/lib-shared-habla';
import {
  memo,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import { AiLoadingState } from 'components/aiLoadingState';
import { CopilotDropdownActionContext } from 'components/copilotDropdown';
import { useAppNavigate } from 'utils/url/useAppNavigate';

import { CopilotDropdownHeader } from '../../../copilotDropdown/components';
import { useQuickAiDraft } from '../../hooks/useQuickAiDraft';

interface EditorCopilotDropdownQuickAiDraftProps {
  dixaConversationId: string;
}

/**
 * Represents a component that drafts a reply with the default AI agent and
 * inserts it into the editor.
 */
export const EditorCopilotDropdownQuickAiDraft = memo(
  function EditorCopilotDropdownQuickAiDraft({
    dixaConversationId,
  }: EditorCopilotDropdownQuickAiDraftProps) {
    const { onBack } = useContext(CopilotDropdownActionContext);
    const dropdownContext = useContext(DropdownButtonContext);
    const navigate = useAppNavigate();
    const [hasFailed, setHasFailed] = useState(false);

    const handleInserted = useCallback(() => {
      dropdownContext.closeDropdown();
    }, [dropdownContext]);

    const handleSettingsClick = useCallback(() => {
      dropdownContext.closeDropdown();
      navigate({ path: '/settings/preferences/agent-copilot' });
    }, [dropdownContext, navigate]);

    const handleError = useCallback(() => {
      setHasFailed(true);
    }, []);

    const { generateDraft, hasAgent, isLoading } = useQuickAiDraft({
      dixaConversationId,
      onInserted: handleInserted,
      onError: handleError,
    });

    const hasStartedRef = useRef(false);

    // Starts the draft once an AI agent is available.
    useEffect(() => {
      if (hasStartedRef.current || !hasAgent) {
        return;
      }

      hasStartedRef.current = true;
      generateDraft();
    }, [hasAgent, generateDraft]);

    const content = (): JSX.Element => {
      if (hasFailed) {
        return (
          <Banner
            variant="fail"
            description={
              <span>An error occurred while generating the reply.</span>
            }
          />
        );
      }

      if (!hasAgent && !isLoading) {
        return (
          <Banner
            variant="info"
            description={<span>No default AI agent set.</span>}
          />
        );
      }

      return <AiLoadingState label="Generating draft" />;
    };

    return (
      <Box style={{ maxWidth: 768, minWidth: 360 }}>
        <CopilotDropdownHeader
          title="Quick Mim drafts"
          onBack={onBack}
          actions={
            <Tooltip content="Go to settings to change the default AI agent">
              <IconButton
                icon="cog"
                variant="tertiary"
                onClick={handleSettingsClick}
              />
            </Tooltip>
          }
        />

        <Box padding="300">{content()}</Box>
      </Box>
    );
  },
);
